/**
 * Circuit Detection Service (Frontend)
 *
 * Mirrors backend circuit detection logic for real-time UI feedback
 * Detects experience subcategory based on user selections in the wizard
 *
 * Circuits:
 * - booking: Standard time-slot based experiences
 * - paintball: Group-based experiences with mixing limits
 * - bowling: Per-person time-slot experiences without mixing limits
 * - simpleticket: Simple ticketing without time slots
 * - events: Single schedule for everyone
 */

export const CIRCUIT_TYPES = {
    BOOKING: 'booking',
    PAINTBALL: 'paintball',
    BOWLING: 'bowling',
    SIMPLE_TICKET: 'simpleticket',
    EVENTS: 'events',
}

export const CONFIDENCE_LEVELS = {
    EXACT: 'exact',
    HIGH: 'high',
    MEDIUM: 'medium',
    LOW: 'low',
}

/**
 * Type configurations matching backend logic
 */
const TYPE_CONFIGS = {
    [CIRCUIT_TYPES.EVENTS]: {
        required: {
            reservation_mode: 'one_schedule_for_everyone',
        },
        optional: {},
        weight: 100, // Strongest signal
    },
    [CIRCUIT_TYPES.PAINTBALL]: {
        required: {
            reservation_mode: 'multiple_time_slots',
            selectedOptionTarifMode: 'price per person',
        },
        optional: {
            isActiveProhibitMixingMinPeople: true,
            prohibitMixingMinPeople: (value) => value > 0,
        },
        weight: 80,
    },
    // Bowling : créneaux + « Durée dynamique » cochée, toujours tarifé PAR GROUPE (2026-09-15).
    // La case le sépare de booking (même socle, poids 70) : poids 80, écart 10 > seuil d'ambiguïté
    // (isAmbiguous compare |premier - second| <= 5, INCLUSIF). Sans la case, score 0.
    // NB : ce service ne sert qu'à l'indication temps réel de l'assistant. La détection qui fait
    // autorité est getPrimaryDetectedType dans store/modules/experience.js.
    [CIRCUIT_TYPES.BOWLING]: {
        required: {
            reservation_mode: 'multiple_time_slots',
            selectedOptionTarifMode: 'price per group',
            dynamic_duration_enabled: 1,
        },
        optional: {},
        weight: 80,
    },
    [CIRCUIT_TYPES.SIMPLE_TICKET]: {
        required: {
            reservation_mode: 'free_access_all_day',
        },
        optional: {},
        weight: 90,
    },
    [CIRCUIT_TYPES.BOOKING]: {
        required: {
            reservation_mode: 'multiple_time_slots',
            selectedOptionTarifMode: 'price per group',
        },
        optional: {},
        weight: 70,
    },
}

class CircuitDetectionService {
    /**
     * Calculate type score based on user selections
     */
    calculateTypeScore(config, userSelections) {
        let score = 0
        const maxScore = config.weight
        let matchedRequired = 0
        const totalRequired = Object.keys(config.required).length

        // Check required fields
        for (const [key, expectedValue] of Object.entries(config.required)) {
            const actualValue = userSelections[key]

            if (actualValue === expectedValue) {
                matchedRequired++
            } else {
                // Required field doesn't match, return 0
                return 0
            }
        }

        // All required fields matched
        score = maxScore * 0.7 // 70% of weight for required fields

        // Check optional fields (add bonus points)
        let matchedOptional = 0
        const totalOptional = Object.keys(config.optional).length

        if (totalOptional > 0) {
            for (const [key, expectedValue] of Object.entries(config.optional)) {
                const actualValue = userSelections[key]

                let matches = false
                if (typeof expectedValue === 'function') {
                    matches = expectedValue(actualValue)
                } else {
                    matches = actualValue === expectedValue
                }

                if (matches) {
                    matchedOptional++
                }
            }

            // Optional fields add remaining 30% proportionally
            const optionalBonus = (matchedOptional / totalOptional) * (maxScore * 0.3)
            score += optionalBonus
        } else {
            // No optional fields, give full weight
            score = maxScore
        }

        return score
    }

    /**
     * Calculate scores for all types
     */
    calculateScores(userSelections) {
        const scores = {}

        for (const [type, config] of Object.entries(TYPE_CONFIGS)) {
            scores[type] = this.calculateTypeScore(config, userSelections)
        }

        return scores
    }

    /**
     * Get confidence level based on score
     */
    getConfidenceLevel(score, maxPossibleScore = 100) {
        const percentage = (score / maxPossibleScore) * 100

        if (percentage === 100) return CONFIDENCE_LEVELS.EXACT
        if (percentage >= 80) return CONFIDENCE_LEVELS.HIGH
        if (percentage >= 60) return CONFIDENCE_LEVELS.MEDIUM
        return CONFIDENCE_LEVELS.LOW
    }

    /**
     * Check if detection is ambiguous (multiple types with similar scores)
     */
    isAmbiguous(scores, bestScore, threshold = 5) {
        const sortedScores = Object.values(scores).sort((a, b) => b - a)

        if (sortedScores.length < 2) return false

        const [first, second] = sortedScores
        return Math.abs(first - second) <= threshold
    }

    /**
     * Get best detection with metadata
     */
    getBestDetection(userSelections) {
        const scores = this.calculateScores(userSelections)

        let bestType = null
        let bestScore = 0

        for (const [type, score] of Object.entries(scores)) {
            if (score > bestScore) {
                bestScore = score
                bestType = type
            }
        }

        if (!bestType || bestScore === 0) {
            return {
                success: false,
                reason: 'no_match',
                scores,
            }
        }

        const isAmbiguous = this.isAmbiguous(scores, bestScore)
        const confidence = this.getConfidenceLevel(bestScore)

        return {
            success: true,
            type: bestType,
            score: bestScore,
            confidence,
            isAmbiguous,
            scores,
            allScores: scores,
        }
    }

    /**
     * Detect from Vuex store state
     */
    detectFromStore(store) {
        const userSelections = {
            reservation_mode: store.getters['experience/reservationMode'],
            selectedOptionTarifMode: store.getters['experience/selectedOptionTarifMode'],
            isActiveProhibitMixingMinPeople: store.getters['experience/isActiveProhibitMixingMinPeople'],
            prohibitMixingMinPeople: store.getters['experience/prohibitMixingMinPeople'],
        }

        return this.getBestDetection(userSelections)
    }

    /**
     * Get human-readable type name
     */
    getTypeName(type) {
        const names = {
            [CIRCUIT_TYPES.BOOKING]: 'Réservation',
            [CIRCUIT_TYPES.PAINTBALL]: 'Paintball',
            [CIRCUIT_TYPES.BOWLING]: 'Bowling',
            [CIRCUIT_TYPES.SIMPLE_TICKET]: 'Ticket Simple',
            [CIRCUIT_TYPES.EVENTS]: 'Événement',
        }

        return names[type] || type
    }

    /**
     * Get type description
     */
    getTypeDescription(type) {
        const descriptions = {
            [CIRCUIT_TYPES.BOOKING]: 'Expérience avec créneaux horaires multiples et tarification par groupe',
            [CIRCUIT_TYPES.PAINTBALL]: 'Expérience de groupe avec limite de mélange et tarification par personne',
            [CIRCUIT_TYPES.BOWLING]: 'Expérience à créneaux, durée selon le nombre de personnes, tarification par groupe',
            [CIRCUIT_TYPES.SIMPLE_TICKET]: 'Ticket simple sans créneaux horaires spécifiques',
            [CIRCUIT_TYPES.EVENTS]: 'Événement avec horaire unique pour tous les participants',
        }

        return descriptions[type] || ''
    }

    /**
     * Get type icon
     */
    getTypeIcon(type) {
        const icons = {
            [CIRCUIT_TYPES.BOOKING]: 'calendar',
            [CIRCUIT_TYPES.PAINTBALL]: 'users',
            [CIRCUIT_TYPES.BOWLING]: 'users',
            [CIRCUIT_TYPES.SIMPLE_TICKET]: 'ticket',
            [CIRCUIT_TYPES.EVENTS]: 'calendar-day',
        }

        return icons[type] || 'question'
    }

    /**
     * Validate if current selections are sufficient for detection
     */
    canDetect(userSelections) {
        const requiredFields = ['reservation_mode', 'selectedOptionTarifMode']

        for (const field of requiredFields) {
            if (!userSelections[field]) {
                return {
                    canDetect: false,
                    missingFields: requiredFields.filter((f) => !userSelections[f]),
                }
            }
        }

        return {
            canDetect: true,
            missingFields: [],
        }
    }
}

// Export singleton instance
export const circuitDetectionService = new CircuitDetectionService()

// Export class for testing
export default CircuitDetectionService
